// src/components/Chat/components/ChatUI/ChatMessages.jsx
import React, { useEffect, useMemo } from "react";
import { FiCheck, FiCheckCircle } from "react-icons/fi";

const ChatMessages = ({
  groupedMessages,
  messagesEndRef,
  currentUser,
  activeUser,
  uploadingFiles,
  uploadProgress,
  updateMessageStatus,
}) => {
  // Format time
  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  // Render file preview
  const renderFilePreview = (message) => {
    if (!message.fileUrl && !message.fileName) return null;

    const fileType = message.type || "file";
    const fileName = message.fileName || "File";
    const fileUrl = message.fileUrl;

    if (fileType === "image") {
      return (
        <div className="mt-2">
          <a
            href={fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="block"
          >
            <img
              src={fileUrl}
              alt={fileName}
              className="max-w-[200px] max-h-[200px] rounded-lg object-cover border border-blue-200"
              onLoad={() => {
                if (message.status === "delivered") {
                  updateMessageStatus?.(message._id, "read");
                }
              }}
            />
          </a>
          <p className="text-xs text-gray-600 mt-1">{fileName}</p>
        </div>
      );
    } else if (fileType === "video") {
      return (
        <div className="mt-2">
          <video
            controls
            className="max-w-[200px] max-h-[200px] rounded-lg"
            src={fileUrl}
          />
          <p className="text-xs text-gray-600 mt-1">{fileName}</p>
        </div>
      );
    } else if (fileType === "audio") {
      return (
        <div className="mt-2">
          <audio controls className="w-full" src={fileUrl} />
          <p className="text-xs text-gray-600 mt-1">{fileName}</p>
        </div>
      );
    } else {
      return (
        <div className="mt-2">
          <a
            href={fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg border border-blue-200 hover:bg-blue-100 transition-colors"
          >
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
              <span className="text-blue-600 font-bold">📎</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {fileName}
              </p>
              <p className="text-xs text-gray-500">
                {message.fileType || "Document"}
              </p>
            </div>
            <span className="text-blue-600 text-sm font-medium">↓</span>
          </a>
        </div>
      );
    }
  };

  // Render upload progress
  const renderUploadProgress = (message) => {
    if (message.status !== "uploading") return null;

    const progress = uploadProgress[message._id] || 0;

    return (
      <div className="mt-2">
        <div className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg border border-blue-200">
          <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
            <span className="text-blue-600 font-bold">📤</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              {uploadingFiles[message._id] || "Uploading..."}
            </p>
            <div className="w-full bg-blue-200 rounded-full h-2 mt-1">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
            <p className="text-xs text-gray-500 mt-1">{progress}%</p>
          </div>
        </div>
      </div>
    );
  };

  // Render message status
  const renderStatus = (message) => {
    if (message.senderId !== currentUser.id) return null;

    switch (message.status) {
      case "sending":
      case "uploading":
        return <span className="text-xs text-gray-400">🕐</span>;
      case "sent":
        return <FiCheck className="text-gray-400 text-xs" />;
      case "delivered":
        return <FiCheck className="text-blue-400 text-xs" />;
      case "read":
        return <FiCheckCircle className="text-green-500 text-xs" />;
      case "error":
        return <span className="text-xs text-red-500">❌</span>;
      default:
        return <FiCheck className="text-gray-400 text-xs" />;
    }
  };

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [groupedMessages, messagesEndRef]);

  return (
    <div className="p-4 space-y-6">
      {groupedMessages.map((group, groupIndex) => (
        <div key={groupIndex} className="space-y-4">
          {/* Date separator */}
          <div className="flex justify-center">
            <div className="bg-blue-100 text-blue-800 text-xs font-semibold px-3 py-1 rounded-full">
              {group.date}
            </div>
          </div>

          {/* Messages in group */}
          {group.messages.map((message, msgIndex) => {
            const isOwn = message.senderId === currentUser.id;
            const isFile = message.type !== "text";
            const isUploading = message.status === "uploading";

            return (
              <div
                key={message._id || msgIndex}
                className={`flex ${isOwn ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[70%] rounded-2xl px-4 py-2.5 ${
                    isOwn
                      ? "bg-gradient-to-r from-blue-100 to-blue-50 border border-blue-200 rounded-br-none"
                      : "bg-gradient-to-r from-yellow-50 to-yellow-100 border border-yellow-200 rounded-bl-none"
                  }`}
                >
                  {/* Sender name for others' messages */}
                  {!isOwn && (
                    <p className="text-xs font-semibold text-blue-900 mb-1">
                      {activeUser.name}
                    </p>
                  )}

                  {/* Message content */}
                  {isFile ? (
                    isUploading ? (
                      renderUploadProgress(message)
                    ) : (
                      renderFilePreview(message)
                    )
                  ) : (
                    <p className="text-sm text-gray-900 whitespace-pre-wrap">
                      {message.message}
                    </p>
                  )}

                  {/* Message footer */}
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-gray-500">
                      {formatTime(message.createdAt || message.timestamp)}
                    </span>
                    <div className="flex items-center gap-1">
                      {renderStatus(message)}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ))}
      <div ref={messagesEndRef} />
    </div>
  );
};

export default ChatMessages;
